import { getSessionAuthentication, baseUrl } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    isActive?: boolean;
}

export interface AddressType {
    id: number;
    typeName: string;
}

export interface ContactInfoType {
    id: number;
    typeName: string;
}

export interface Address {
    id: number;
    addressType: AddressType;
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    postalCode?: string;
    city?: string;
    country?: string;
}

export interface ContactInfo {
    id: number;
    contactInfoType: ContactInfoType;
    value?: string;
}

export interface Pricelist {
    id: number;
    pricelistName: string;
}

export interface LocationType {
    id: number;
    description: string;
}

export interface LocationCategory {
    id: number;
    category: string;
}

export interface SalesChannel {
    id: number;
    salesChannelCode?: string;
    salesChannelDescription?: string;
}

export interface ResellerCustomer {
    id: number;
    firstName?: string;
    lastName?: string;
    socialSecurityNumber?: string;
}

export interface Location {
    id: number;
    locationName: string;
    locationNumber: number;
    isActive: boolean;
    shouldGenerateAutoOrders: boolean;
    locationType?: LocationType | null;
    locationCategory?: LocationCategory | null;
    salesChannel?: SalesChannel | null;
    resellerCustomer?: ResellerCustomer | null;
    // Backend reads this as `ResellerCostPriceFactor` (capital R). Preserve
    // the casing so serialization round-trips correctly.
    ResellerCostPriceFactor?: number;
    storeManager?: User | null;
    squareMeters: number;
    startDate?: string | null;
    organizationNumber?: string | null;
    resellerPriceList?: Pricelist | null;
    addresses: Address[];
    contactInfos: ContactInfo[];
    users: User[];
}

export async function getLocation(id: number): Promise<Location> {
    return legacyFetch<Location>(`locations/${id}`);
}

export async function updateLocation(loc: Location): Promise<Location> {
    return legacyFetch<Location>(`locations/${loc.id}`, {
        method: 'PUT',
        body: JSON.stringify(loc),
    });
}

export async function getUsers(): Promise<User[]> {
    return legacyFetch<User[]>('locations/users');
}

export async function getAddressTypes(): Promise<AddressType[]> {
    return legacyFetch<AddressType[]>('locations/addresstypes');
}

export async function getContactInfoTypes(): Promise<ContactInfoType[]> {
    return legacyFetch<ContactInfoType[]>('locations/contactinfotypes');
}

export async function getUserLocations(): Promise<number[]> {
    return legacyFetch<number[]>('locations/user/locations');
}

export async function getPricelists(): Promise<Pricelist[]> {
    return legacyFetch<Pricelist[]>('locations/pricelists');
}
